import type { ReactElement } from 'react';
import { KTDSIcon } from '@icons/KTDSIcon';
import { SMPWizIcon } from '@icons/SMPWizIcon';
import { SmpColorIcon } from '@icons/SmpColorIcon';
import { SplitterLogoIcon } from '@icons/SplitterLogoIcon';
import { ZenithFullLogoIcon } from '@icons/ZenithFullLogoIcon';
import { ZenithLogoIcon } from '@icons/ZenithLogoIcon';

type IconProps = {
  width?: number;
  height?: number;
};

type ProviderIconComponent = (props: IconProps) => ReactElement;

/**
 * Icon 타입 (사용 위치에 따라 구분)
 */
export type IconVariant = 'main' | 'login' | 'title';

/**
 * Provider별 Icon 매핑
 * 새로운 Provider 추가 시 여기에만 추가하면 됨
 */
export const PROVIDER_ICONS: Record<string, Partial<Record<IconVariant, ProviderIconComponent>>> = {
  KT_DS: {
    main: props => <KTDSIcon {...props} />,
    login: props => <SMPWizIcon {...props} />,
  },
  MEGAZONE: {
    main: props => <SmpColorIcon {...props} />,
    login: props => <SplitterLogoIcon {...props} />,
  },
  ZENITH: {
    main: props => <ZenithLogoIcon {...props} />,
    login: props => <ZenithFullLogoIcon {...props} />,
  },
  // 새로운 Provider 추가 예시:
  // NEW_PROVIDER: props => <NewProviderIcon {...props} />,
} as const;

/**
 * Provider 이름에 맞는 Icon을 반환 (함수 방식 - 하위 호환)
 * @deprecated 대신 ProviderIcon 컴포넌트를 사용하세요
 */
export const getProviderIcon = (
  providerName: string,
  variant: IconVariant = 'main',
  props: IconProps = { width: 56, height: 56 },
): ReactElement => {
  const providerIcons = PROVIDER_ICONS[providerName] ?? PROVIDER_ICONS.MEGAZONE;
  const IconComponent = providerIcons[variant];
  // variant가 없으면 main으로 fallback
  if (!IconComponent) {
    return providerIcons.main?.(props) ?? PROVIDER_ICONS.MEGAZONE.main!(props);
  }

  return IconComponent(props);
};

/**
 * Provider 이름에 맞는 Icon 컴포넌트
 * Provider가 없으면 기본 Icon 반환
 *
 * @example
 * <ProviderIcon providerName="KT_DS" variant="main" width={56} height={56} />
 */
type ProviderIconProps = IconProps & {
  providerName: string;
  variant?: IconVariant;
};

export const ProviderIcon = ({ providerName, variant = 'main', ...iconProps }: ProviderIconProps): ReactElement => {
  const providerIcons = PROVIDER_ICONS[providerName] ?? PROVIDER_ICONS.MEGAZONE;
  const IconComponent = providerIcons[variant];

  // variant가 없으면 main으로 fallback
  if (!IconComponent) {
    return providerIcons.main?.(iconProps) ?? PROVIDER_ICONS.MEGAZONE.main!(iconProps);
  }

  return IconComponent(iconProps);
};
